const BYOB_CONFETTI_COLORS = ['#ffda00', '#35a38c', '#425cc7', '#8cd1c2', '#ffe890'];
const BYOB_CONFETTI_COUNT = 120;
const BYOB_CONFETTI_DURATION = 2200;

class ByobBoxBar extends HTMLElement {
  constructor() {
    super();
    this.items = new Map();
    this.previousTotal = 0;
    this.isReady = false;
    this.isSubmitting = false;
    this.confettiFrame = null;
    this.onUpdate = this.onUpdate.bind(this);
    this.onSubmitting = this.onSubmitting.bind(this);
    this.onError = this.onError.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.render = this.render.bind(this);
  }

  connectedCallback() {
    // Theme-editor fallback, used until the controller reports the offer's own box sizes and prices.
    this.tiers = {
      low: Number(this.dataset.countLow) || 4,
      high: Number(this.dataset.countHigh) || 6,
      priceLow: null,
      priceHigh: null,
    };
    this.slots = [...this.querySelectorAll('[data-slot]')];
    this.progressBar = this.querySelector('[data-progressbar]');
    this.bottleInputs = [...this.querySelectorAll('[data-bottle-input]')];
    this.cta = this.querySelector('[data-cta]');
    this.ctaLabel = this.querySelector('[data-cta-label]');
    this.ctaPrice = this.querySelector('[data-cta-price]');
    this.noteLead = this.querySelector('[data-note-lead]');
    this.noteText = this.querySelector('[data-note-text]');
    this.noteBottles = this.querySelector('[data-note-bottles]');
    this.bottles = this.querySelector('[data-bottles]');
    this.errorBox = this.querySelector('[data-error]');
    this.canvas = this.querySelector('[data-confetti-canvas]');

    this.cta?.addEventListener('click', this.onSubmit);
    this.bottles?.addEventListener('change', this.render);
    document.addEventListener('byob:update', this.onUpdate);
    document.addEventListener('byob:submitting', this.onSubmitting);
    document.addEventListener('byob:error', this.onError);

    // Slot sizes change with the breakpoint and the slot count, so re-measure the bar whenever the boxes resize.
    this.box = this.querySelector('.byob-sticky-bar__box');
    if (this.box && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.measureBox();
        this.updateProgress();
      });
      this.resizeObserver.observe(this.box);
    }

    this.render();
    document.dispatchEvent(new CustomEvent('byob:request-state'));
  }

  disconnectedCallback() {
    this.cta?.removeEventListener('click', this.onSubmit);
    this.bottles?.removeEventListener('change', this.render);
    document.removeEventListener('byob:update', this.onUpdate);
    document.removeEventListener('byob:submitting', this.onSubmitting);
    document.removeEventListener('byob:error', this.onError);
    this.resizeObserver?.disconnect();
    if (this.confettiFrame) cancelAnimationFrame(this.confettiFrame);
  }

  getTotal() {
    let total = 0;
    for (const item of this.items.values()) total += item.qty;
    return total;
  }

  onUpdate(event) {
    const { items = [], ready = false, tiers } = event.detail ?? {};

    if (tiers) this.tiers = tiers;

    this.items = new Map(items.map((item) => [item.productId, item]));
    this.isReady = ready;

    const total = this.getTotal();
    if (total !== this.previousTotal) this.clearError();
    const reachedTier = total > this.previousTotal && (total === this.tiers.low || total === this.tiers.high);

    this.render();
    this.previousTotal = total;

    if (reachedTier) this.launchConfetti();
  }

  // Bottles are add-ons: they never enter the box, so they do not fill slots or count toward the box size.
  // The controller adds the ticked ones to the cart next to the box when the box is submitted.
  getAddons() {
    return {
      selected: this.bottleInputs.filter((input) => input.checked).map((input) => input.dataset.variantId),
      all: this.bottleInputs.map((input) => input.dataset.variantId),
    };
  }

  onSubmitting(event) {
    this.isSubmitting = event.detail.state;
    const isOwnSubmit = event.detail.source === 'bar';
    this.cta?.classList.toggle('byob-sticky-bar__cta--loading', this.isSubmitting && isOwnSubmit);
    if (this.isSubmitting && isOwnSubmit) this.clearError();
    this.render();
  }

  onError(event) {
    this.showError(event.detail.message);
  }

  getSlotOfUnit(unit, entries, total) {
    let covered = 0;
    for (const [index, item] of entries.entries()) {
      covered += item.qty;
      if (unit <= covered) return index;
    }
    return entries.length + (unit - total) - 1;
  }

  // The box width and slot gap only change on resize, so they are read once here and not on every render.
  measureBox() {
    const slots = this.box?.querySelector('.byob-sticky-bar__slots');
    const width = this.box?.getBoundingClientRect().width;
    if (!slots || !width) return;
    this.boxSize = { width, gap: parseFloat(getComputedStyle(slots).columnGap) || 0 };
  }

  // One continuous bar as wide as a full box of slots, whatever the slot size. The fill and the stops sit at the
  // right edge of the unit's slot, so the fill reaches a stop exactly when that box size is complete.
  updateProgress() {
    if (!this.progressBar) return;

    const total = this.getTotal();
    const { low, high } = this.tiers;
    if (this.box?.style.getPropertyValue('--byob-count') !== String(high)) {
      this.box?.style.setProperty('--byob-count', high);
      this.boxSize = null;
    }
    if (!this.boxSize) this.measureBox();

    const { width, gap } = this.boxSize ?? {};
    const slotWidth = (width - (high - 1) * gap) / high;
    const edge = (unit) => {
      if (!width || !(slotWidth > 0)) return Math.min(unit / high, 1);
      return Math.min((unit * slotWidth + (unit - 1) * gap) / width, 1);
    };

    this.progressBar.style.setProperty('--byob-fill', total ? edge(Math.min(total, high)) : 0);
    this.progressBar.style.setProperty('--byob-pos-low', edge(low));
    this.progressBar.style.setProperty('--byob-pos-high', 1);
  }

  // Price from the offer, shown in the store currency; falls back to the label typed in the theme editor.
  formatTierPrice(amount, fallback) {
    if (amount == null) return fallback;
    const currency = window.Shopify?.currency?.active;
    if (!currency) return fallback;
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  }

  fillTokens(template, count, price, bottles = '') {
    return (template ?? '')
      .replaceAll('[count]', count)
      .replaceAll('[price]', price ?? '')
      .replaceAll('[bottles]', bottles);
  }

  render() {
    const total = this.getTotal();
    const entries = [...this.items.values()];
    const { low, high } = this.tiers;
    const { msgTier, msgDone, msgNext, msgBottles, msgBottle, btnMore, btnReady } = this.dataset;
    const priceLow = this.formatTierPrice(this.tiers.priceLow, this.dataset.priceLow);
    const priceHigh = this.formatTierPrice(this.tiers.priceHigh, this.dataset.priceHigh);
    const isValidSize = total === low || total === high;
    const isCtaReady = this.isReady && isValidSize && !this.isSubmitting;
    const visibleCount = entries.length + (high - total);
    const tierLowIndex = this.getSlotOfUnit(low, entries, total);
    const tierHighIndex = this.getSlotOfUnit(high, entries, total);

    for (const [index, slot] of this.slots.entries()) {
      const item = entries[index];
      const image = slot.querySelector('[data-slot-image]');
      const badge = slot.querySelector('[data-slot-badge]');

      slot.hidden = index >= visibleCount;
      slot.classList.toggle('byob-sticky-bar__slot--filled', Boolean(item));
      slot.classList.toggle('byob-sticky-bar__slot--tier-1', index <= tierLowIndex);

      if (image) {
        if (item?.thumbUrl) {
          image.src = item.thumbUrl;
          image.alt = item.title;
          image.hidden = false;
        } else {
          image.removeAttribute('src');
          image.alt = '';
          image.hidden = true;
        }
      }

      if (badge) {
        badge.textContent = item ? `×${item.qty}` : '';
        badge.hidden = !item || item.qty < 2;
      }
    }

    if (this.progressBar) {
      this.updateProgress();
      for (const stop of ['low', 'high']) {
        const marker = this.progressBar.querySelector(`[data-stop-marker="${stop}"]`);
        const price = this.progressBar.querySelector(`[data-stop-price="${stop}"]`);
        if (marker) marker.textContent = this.tiers[stop];
        if (price) price.textContent = stop === 'high' ? priceHigh : priceLow;
      }
    }

    // Under the button: the price reached so far and what is left to unlock, then the box plus the ticked bottles.
    if (this.noteText) {
      let lead = '';
      let text;
      if (total >= high) {
        lead = '✓';
        text = this.fillTokens(msgDone, 0, priceHigh);
      } else if (total >= low) {
        if (total === low) lead = `✓ ${priceLow}`;
        text = this.fillTokens(msgNext, high - total, priceHigh);
      } else {
        text = this.fillTokens(msgTier, low - total, priceLow);
      }
      this.noteLead.textContent = lead;
      this.noteLead.classList.toggle('byob-sticky-bar__note-lead--done', total >= high);
      this.noteText.textContent = text;
    }

    const picked = total >= low ? this.bottleInputs.filter((input) => input.checked) : [];
    const bottlesAmount = picked.reduce((sum, input) => sum + (Number(input.dataset.price) || 0), 0) / 100;

    if (this.noteBottles) {
      const boxPrice = total >= high ? priceHigh : priceLow;
      this.noteBottles.hidden = !picked.length;
      this.noteBottles.textContent = picked.length
        ? this.fillTokens(picked.length === 1 ? msgBottle : msgBottles, picked.length, boxPrice, this.formatTierPrice(bottlesAmount, String(bottlesAmount)))
        : '';
    }

    if (this.cta) {
      const missing = total < low ? low - total : high - total;
      // The button shows the box price plus the ticked bottles once the box is complete.
      const boxAmount = total >= high ? this.tiers.priceHigh : this.tiers.priceLow;
      const showPrice = isValidSize && boxAmount != null;
      this.ctaLabel.textContent = isValidSize ? btnReady : this.fillTokens(btnMore, missing, '');
      this.ctaPrice.textContent = showPrice ? ` • ${this.formatTierPrice(boxAmount + bottlesAmount, '')}` : '';
      this.cta.setAttribute('aria-disabled', String(!isCtaReady));
      this.cta.classList.toggle('byob-sticky-bar__cta--ready', isCtaReady);
    }

    this.bottles?.classList.toggle('byob-sticky-bar__bottles--visible', total >= low);
  }

  showError(message) {
    if (!this.errorBox) return;
    this.errorBox.textContent = message;
    this.errorBox.hidden = false;
  }

  clearError() {
    if (!this.errorBox) return;
    this.errorBox.textContent = '';
    this.errorBox.hidden = true;
  }

  onSubmit() {
    if (this.cta.getAttribute('aria-disabled') === 'true') return;
    document.dispatchEvent(new CustomEvent('byob:submit', { detail: { source: 'bar', addons: this.getAddons() } }));
  }

  launchConfetti() {
    if (this.dataset.confetti !== 'true' || !this.canvas) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const context = this.canvas.getContext('2d');
    if (!context) return;

    if (this.confettiFrame) cancelAnimationFrame(this.confettiFrame);

    const width = window.innerWidth;
    const height = window.innerHeight;
    this.canvas.width = width;
    this.canvas.height = height;

    const particles = Array.from({ length: BYOB_CONFETTI_COUNT }, () => ({
      x: Math.random() * width,
      y: -10 - Math.random() * height * 0.3,
      vx: (Math.random() - 0.5) * 4,
      vy: 2 + Math.random() * 4,
      size: 6 + Math.random() * 6,
      angle: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * 0.3,
      color: BYOB_CONFETTI_COLORS[Math.floor(Math.random() * BYOB_CONFETTI_COLORS.length)],
    }));

    const start = performance.now();

    const draw = (now) => {
      const progress = (now - start) / BYOB_CONFETTI_DURATION;
      context.clearRect(0, 0, width, height);

      if (progress >= 1) {
        this.confettiFrame = null;
        return;
      }

      context.globalAlpha = progress > 0.7 ? (1 - progress) / 0.3 : 1;

      for (const particle of particles) {
        particle.vy += 0.06;
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.angle += particle.spin;

        context.save();
        context.translate(particle.x, particle.y);
        context.rotate(particle.angle);
        context.fillStyle = particle.color;
        context.fillRect(-particle.size / 2, -particle.size / 4, particle.size, particle.size / 2);
        context.restore();
      }

      this.confettiFrame = requestAnimationFrame(draw);
    };

    this.confettiFrame = requestAnimationFrame(draw);
  }
}

if (!customElements.get('byob-box-bar')) {
  customElements.define('byob-box-bar', ByobBoxBar);
}
